import React, {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledHydrateBarWrapper
  from 'components/templates/Wrappers/Hydrate/HydrateBarWrapper/HydrateBarWrapper.styles';
import {captureHydration, hydrationRoutes} from 'helpers/hydrate';
import {useHydrationWalk, useHydrationGenerate} from 'helpers/hooks/hydrate';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import {useRouter} from 'components/organisms/Providers/RouteProvider/RouteProvider';
import {useThemeControl, useThemes} from 'components/organisms/Providers/ThemeProvider/ThemeProvider';
import {useEntityCallbacks} from 'services/entity/entity.utils';
import constants from 'helpers/constants';
import {recordedProps} from 'helpers/styles';
import DirectionsWalk from '@mui/icons-material/DirectionsWalk';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DataObject from '@mui/icons-material/DataObject';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';

const HydrateBarWrapper = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'HydrateBarWrapper');

  const [collection, setCollection] = useState(null);
  const [dark, setDark] = useState(false);
  const [captured, setCaptured] = useState(null);

  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();
  const router = useRouter();
  const themes = useThemes();
  const themeControl = useThemeControl();
  const hydrationWalk = useHydrationWalk();
  const generate = useHydrationGenerate();
  const callbacks = useEntityCallbacks();

  // Walking or generating locks the controls: starting one mid-run, or changing the white
  // label / collection under it, would corrupt the sheet the capture reads.
  const busy = hydrationWalk.running ||
    generate.status === 'loading' || generate.status === 'rendering';

  // Walking dark doubles the steps, so it is off unless asked for.
  const themeNames = useMemo(() => {
    const names = (themes ?? []).map((theme) => {
      return theme.name;
    });

    return dark ? names : names.slice(0, 1);
  }, [themes, dark]);

  const rightFields = useMemo(() => {
    return [
      {
        name: 'collection',
        inlineLabel: 'Collection',
        placeholder: 'Search for a collection',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.collection,
        initial: collection,
        disabled: busy,
        options: callbacks.collections,
        FormFieldProps: {
          multiple: false,
          autoFocus: false,
          hiddenLabel: true,
          clearable: false,
          size: 'small'
        }
      }
    ];
  }, [collection, callbacks.collections, busy]);

  // With no collection picked the walk starts at the list and clicks the first row, the same
  // way it gets to an entity - so there is always a collection to walk.
  const routes = useMemo(() => {
    return hydrationRoutes.map((route) => {
      if (route.path.includes(':collectionId')) {
        if (collection?.collectionId) {
          return {...route, path: route.path.replace(':collectionId', collection.collectionId)};
        } else {
          return {...route, path: '/collections', firstRow: true};
        }
      } else {
        return route;
      }
    });
  }, [collection?.collectionId]);

  const handleWalk = useCallback(() => {
    hydrationWalk.start({
      router,
      routes,
      themes: themeNames,
      setTheme: themeControl.setName,
      closeDialog: dialogControl.hide,
      home: '/dev/hydrate'
    });
  }, [hydrationWalk, router, routes, themeNames, themeControl, dialogControl]);

  const handleProps = useCallback(() => {
    const props = recordedProps();
    const count = Object.keys(props).length;

    navigator.clipboard.writeText(JSON.stringify(props, null, 2))
      .then(() => {
        snackbar.show(`Copied props for ${count} components to clipboard`, null,
          {color: 'success', autoHideDuration: constants.delay.short});
      })
      .catch(() => {
        snackbar.show('Copy to clipboard failed', null,
          {color: 'error', autoHideDuration: constants.delay.error});
      });
  }, [snackbar]);

  // Reloads to a fresh sheet and auto-starts the render. A hard navigation is deliberate: the
  // theme is resolved at boot.
  const handleGenerate = useCallback(() => {
    window.location.assign('/dev/hydrate?generate=1');
  }, []);

  const handleCapture = useCallback(() => {
    const hydration = captureHydration();
    setCaptured(hydration);

    navigator.clipboard.writeText(JSON.stringify(hydration, null, 2))
      .then(() => {
        snackbar.show(`Copied ${hydration.rules} rules to clipboard`, null,
          {color: 'success', autoHideDuration: constants.delay.short});
      })
      .catch(() => {
        snackbar.show('Copy to clipboard failed', null,
          {color: 'error', autoHideDuration: constants.delay.error});
      });
  }, [snackbar]);

  const rightActions = useMemo(() => {
    return [
      {
        label: hydrationWalk.running ? 'Walking…' : 'Walk',
        tooltip: 'Visit the big pages so their styles land in the sheet',
        icon: DirectionsWalk,
        auth: null,
        onClick: () => {
          handleWalk();
        },
        ButtonProps: {
          disabled: busy
        }
      },
      {
        label: 'Props',
        tooltip: 'Copy recorded props for src/theme/hydration.props.json',
        icon: DataObject,
        auth: null,
        onClick: () => {
          handleProps();
        },
        ButtonProps: {
          disabled: busy
        }
      },
      {
        label: generate.status === 'loading' || generate.status === 'rendering' ? 'Generating…' : 'Generate',
        tooltip: 'Reload and render every recorded component so their styles land in the sheet',
        icon: AutoFixHigh,
        auth: null,
        onClick: () => {
          handleGenerate();
        },
        ButtonProps: {
          disabled: busy
        }
      },
      {
        label: 'Capture',
        tooltip: 'Copy the sheet for src/theme/hydration.json',
        icon: ContentCopy,
        auth: null,
        onClick: () => {
          handleCapture();
        },
        ButtonProps: {
          disabled: busy
        }
      }
    ];
  }, [hydrationWalk.running, generate.status, busy, handleWalk, handleProps, handleGenerate, handleCapture]);

  const rightSwitchActions = useMemo(() => {
    return [
      {
        label: 'Dark',
        active: dark,
        onClick: (e) => {
          setDark((current) => {
            return !current;
          });
          e.preventDefault();
        },
        SwitchProps: {
          disabled: busy
        }
      }
    ];
  }, [dark, busy]);

  const handleFieldChange = useCallback((field, value) => {
    if (field.name === 'collection') {
      setCollection(value.collection);
    }
  }, []);

  const title = captured ?
    `Hydrate — ${captured.groups} components, ${captured.rules} rules, ` +
      `${Math.round(captured.css.length / 1024)}kb` :
    'Hydrate';

  return <StyledHydrateBarWrapper ref={ref} {...innerProps}
                                  ToolbarProps={{
                                    title,
                                    rightFields,
                                    onRightChange: handleFieldChange,
                                    rightActions,
                                    rightSwitchActions
                                  }}>
    {innerProps.children}
  </StyledHydrateBarWrapper>
});

HydrateBarWrapper.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default HydrateBarWrapper;
